/** Shared types for instrument recipes. */
import type { NoiseBank } from '../noise';
import type { Rng } from '../util';
import type { Route, Voice, VoicePool } from '../voice';
import type { WaveBank } from '../waves';
import type { PluckCache } from './pluck';

/** Per-context resources every recipe can use (created once). */
export interface Kit {
  readonly ctx: BaseAudioContext;
  readonly noise: NoiseBank;
  readonly waves: WaveBank;
  readonly plucks: PluckCache;
  readonly rng: Rng;
}

/** A place to make sound: resources + a voice pool + an output route. */
export interface Synth {
  readonly kit: Kit;
  readonly pool: VoicePool;
  readonly route: Route;
}

export interface NoteOpts {
  readonly pan?: number;
  /** Reverb send (0..1). */
  readonly send?: number;
  /** Decay-length multiplier. */
  readonly decay?: number;
  /** Pitch bend (semitones) applied after the attack — cartoony "aww" drops. */
  readonly bend?: number;
  /** Darker, shorter, softer variant. */
  readonly muted?: boolean;
}

export function withRoute(s: Synth, route: Route): Synth {
  return { kit: s.kit, pool: s.pool, route };
}

/** Looping noise source from a random offset, so repeated one-shots never sound identical. */
export function noiseSrc(v: Voice, kit: Kit, buf: AudioBuffer, t0: number, t1: number, rate = 1): AudioBufferSourceNode {
  const src = v.src(buf, true, rate);
  v.play(src, t0, t1, kit.rng.next() * buf.duration * 0.9);
  return src;
}

/** Connect nodes in series. */
export function link(...nodes: AudioNode[]): void {
  for (let i = 0; i < nodes.length - 1; i++) {
    const a = nodes[i];
    const b = nodes[i + 1];
    if (a && b) a.connect(b);
  }
}
